/**
 * Consent API Schemas
 *
 * Zod schemas for consent management endpoints.
 * SINGLE SOURCE OF TRUTH for consent API contract.
 */

import { z } from 'zod/v4';

// ============================================================================
// Consent Event Schemas
// ============================================================================

export const consentEventTypeSchema = z.enum([
  'granted',
  'withdrawn',
  'expired',
  'verified',
]);

/**
 * Что может стоять в `consent_events.consent_type`.
 *
 * Шире, чем то, что продукт умеет записать СЕГОДНЯ: `analysis`, `storage` и
 * `crm_delivery` не пишет ни один код-путь, но строки этих типов в журнале
 * есть (прод, org sezonua, `source: manual_backfill`). Журнал append-only и
 * показывается как есть, поэтому значения остаются в схеме чтения — убрать их
 * отсюда значило бы, что Zod уронит ВЕСЬ ответ журнала из-за одной старой
 * строки. Чего они больше не делают — не участвуют в статусе и не предлагаются
 * к выдаче/отзыву: см. `participantConsentTypeSchema` (#401).
 */
export const consentTypeSchema = z.enum([
  'recording',
  'analysis',
  'storage',
  'crm_delivery',
  // Legacy/account agreement evidence remains readable. These values are not
  // participant consent and therefore are intentionally excluded from the
  // withdraw contract below.
  'terms_of_service',
  'privacy_policy',
  // Opt-in to the marketing series (SendPulse). Deliberately separate from
  // terms_of_service/privacy_policy: those are a precondition of using the
  // product, this one is a free choice, and bundling them is exactly what
  // Bitrix24 Marketplace moderation objected to (2026-08-12). Recorded per
  // PERSON — `grantedBy` is the user, not the org.
  'marketing_email',
]);

/**
 * Согласие участников разговора, которое организация действительно фиксирует.
 *
 * Одно, а не четыре. `analysis`, `storage` и `crm_delivery` стояли здесь, но
 * взять их было неоткуда: подтверждение комплайенса пишет ровно одно событие
 * `recording`, поэтому три карточки из четырёх показывали «не зафиксировано»
 * у КАЖДОЙ организации, сколько бы раз она ни подтверждала (#401).
 *
 * И четвёрка не нужна по существу. Согласие у участников получает Клиент как
 * контроллер — privacy-policy прямо относит это к нему («Obtaining consent
 * from call participants — Customer»), а Aelo ведёт журнал как доказательство.
 * Свою правовую конструкцию организация заявляет тройкой `consentMode` +
 * `jurisdiction` + `legalBasis` и подтверждает одним действием; дробление на
 * «анализ / хранение / доставка в CRM» описывало бы одно и то же основание
 * четырьмя записями, ни одну из которых нельзя дать или отозвать отдельно.
 */
export const participantConsentTypeSchema = z.enum(['recording']);

/**
 * Что можно ВЫДАТЬ через `POST /consent/grant`.
 *
 * Ровно то, что продукт умеет записать по просьбе клиента: согласие с
 * условиями и политикой при онбординге и подписка на рассылку. `recording`
 * сюда не входит — его создаёт подтверждение комплайенса своим маршрутом, а не
 * произвольный вызывающий.
 *
 * Схема появилась потому, что её не было: приём принимал весь
 * `consentTypeSchema`, то есть любой клиент мог создать строку типа, которого
 * продукт не фиксирует, — и после сужения контракта отзыва такая строка стала
 * бы неотзываемой в момент создания (#401).
 */
export const grantableConsentTypeSchema = z.enum([
  'terms_of_service',
  'privacy_policy',
  'marketing_email',
]);

/**
 * Что можно ОТОЗВАТЬ.
 *
 * Шире, чем то, что сегодня можно выдать, и это намеренно: отозвать нужно уметь
 * всё, что когда-либо было записано. В проде есть строки `analysis`, `storage`
 * и `crm_delivery` (org sezonua, `source: manual_backfill`), и журнал показывает
 * их как выданные согласия. Убрать эти значения отсюда значило бы, что
 * зафиксированное согласие невозможно отозвать никаким способом, — прямо против
 * privacy-policy, которая обещает отзыв по Art. 7(3) и «audit trail … including
 * consent withdrawal», и против правила «отзыв обязан быть не сложнее выдачи».
 *
 * Terms и Privacy остаются исключением по другой причине: это доказательства
 * принятия условий аккаунта, а не согласие участника разговора.
 */
export const withdrawableConsentTypeSchema = z.enum([
  'recording',
  'analysis',
  'storage',
  'crm_delivery',
  'marketing_email',
]);

/**
 * Записи журнала, которые принадлежат АККАУНТУ, а не участникам разговоров
 * (#1100): принятие Условий и Политики пользователем и его подписка на
 * рассылку. Сегодня это те же типы, что выдаются через `POST /consent/grant`,
 * но список свой (см. ниже).
 *
 * Журнал один, но на экране это две разные вещи. Страница «Управление
 * согласием» говорит о согласиях участников и держит рядом «Отозвать» —
 * принятие Условий, показанное в том же списке, читалось как отзываемое
 * согласие участника, а это доказательство договора аккаунта.
 */
export const accountAgreementConsentTypeSchema = z.enum([
  'terms_of_service',
  'privacy_policy',
  'marketing_email',
]);
/**
 * Named on its own, not as «what is grantable»: a participant type that became
 * grantable must not slide into this read-only block, and an account type that
 * is not grantable (a DPA acceptance, say) must not land in the participant
 * journal next to «Withdraw». The participant half is its complement.
 */
export const ACCOUNT_AGREEMENT_CONSENT_TYPES = accountAgreementConsentTypeSchema.options;

/**
 * Какую половину журнала читает `GET /consent`: согласия участников
 * (`participant` — всё, кроме договора аккаунта, включая исторические типы)
 * или договор аккаунта (`account`). Без параметра — весь журнал.
 */
export const consentJournalScopeSchema = z.enum(['participant', 'account']);

export const consentEventSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  recordId: z.string().nullable(),
  eventType: consentEventTypeSchema,
  consentType: consentTypeSchema,
  grantedBy: z.string().nullable(),
  metadata: z.unknown().nullable(),
  /**
   * NULL допускает сама колонка (`DEFAULT datetime('now')`, без NOT NULL):
   * строка без отметки — редкость, но не повод отвергать всю страницу журнала
   * вместе с остальными строками (Codex, #1455).
   */
  createdAt: z.string().nullable(),
});

// ============================================================================
// API Request/Response Schemas
// ============================================================================

export const grantConsentRequestSchema = z
  .object({
    consentType: grantableConsentTypeSchema,
    recordId: z.string().uuid().optional(),
    // NOTE: the audit actor (`grantedBy`) is NOT a client input — it is derived
    // from the authenticated session in the route handler. It is later used as an
    // identity filter (getUserConsent), so accepting it from the body let a caller
    // forge a consent record attributed to another user.
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

/**
 * Отзыв согласия. Только тип — и это весь контракт.
 *
 * Здесь стояли ещё `recordId` и `cascadeDelete`, которых маршрут никогда не
 * читал: `revokeConsent(orgId, consentType, grantedBy)` не принимает ни того,
 * ни другого. То есть участник, отзывающий согласие по одной записи с
 * `cascadeDelete: true`, получал 200 «withdrawn», при этом не удалялось ничего,
 * а в журнал попадал отзыв уровня организации. Обещание удаления данных, не
 * подкреплённое кодом, — худший вид молчаливого отказа, потому что человек
 * считает своё право реализованным.
 *
 * Отзыв по конкретной записи и каскадное удаление — отдельная работа с
 * собственным решением о том, что именно удаляется; пока её нет, контракт
 * этого не обещает.
 */
export const withdrawConsentRequestSchema = z
  .object({
    consentType: withdrawableConsentTypeSchema,
  })
  .strict();

/**
 * Текущее положение дел по согласиям организации.
 *
 * Полей ровно столько, сколько продукт умеет зафиксировать. `analysis`,
 * `storage` и `crm_delivery` отсюда убраны: их не писал никто, они всегда были
 * `false`, и экран сообщал это как факт о клиенте (#401).
 */
export const consentStatusResponseSchema = z.object({
  recording: z.boolean(),
  terms_of_service: z.boolean(),
  privacy_policy: z.boolean(),
  complianceConfirmed: z.boolean(),
  /**
   * Действующие согласия типов, которые продукт больше не выдаёт.
   *
   * Почти всегда пусто, и в этом весь смысл: карточка появляется у того, у
   * кого такое согласие ЕСТЬ, а не у каждого. Вечные «не зафиксировано» — то,
   * ради устранения чего затевалось #401; но убрать заодно и возможность
   * отозвать уже записанное значило бы оставить право без способа им
   * воспользоваться (Art. 7(3): отзыв не сложнее выдачи). В проде такие строки
   * есть — org sezonua, `source: manual_backfill`.
   */
  legacyGranted: z.array(withdrawableConsentTypeSchema),
  /**
   * Настройки комплайенса организации не читаются.
   *
   * Не косметика, а условие исполнимости отзыва. При порче `settings_json`
   * (или `consentConfig`, не проходящего схему) `complianceConfirmed`
   * становится `false` — а карточка показывает кнопку «Отозвать» только у
   * подтверждённого согласия. Гейт приёма на той же ошибке разбора идёт ДАЛЬШЕ
   * (`IngestWorker` Step 0.5, fail-open), то есть разговоры продолжают
   * обрабатываться, и единственный способ это остановить оказывался скрыт.
   *
   * Поле поднимает третье состояние — «неизвестно» вместо «не зафиксировано»,
   * — и по нему интерфейс оставляет действие доступным: сам отзыв к порче
   * терпим (карантин блоба + сброс к умолчаниям) и приводит настройки в
   * определённое состояние.
   */
  complianceSettingsUnreadable: z.boolean(),
  /** Latest recording decision is an explicit withdrawal, not missing setup. */
  recordingWithdrawn: z.boolean().optional().default(false),
});

/**
 * Своя подписка на рассылку: `GET /api/v1/consent/marketing`.
 *
 * Отдельный ответ, а не поле в `consentStatusResponseSchema`: тот описывает
 * организацию, этот — человека. `email` равен `null`, когда подписывать не на
 * что: у портального пользователя под минимальным scope Bitrix24 в базе лежит
 * плейсхолдер, и отдавать его наружу как адрес нельзя — интерфейс по этому
 * полю решает, показывать ли предложение вообще.
 */
export const marketingSubscriptionResponseSchema = z.object({
  subscribed: z.boolean(),
  email: z.string().nullable(),
});

/**
 * Позиция в журнале согласий (#1397): после какой строки читать дальше и в
 * какой выборке её выдали.
 *
 * Строка названа своим `id`, а не временем и `rowid`: сервер сам находит её
 * место в порядке «новые сверху». `rowid` в курсоре был бы счётчиком, общим
 * для всех организаций, — по разнице двух курсоров любой клиент читал бы, сколько
 * событий согласия пишет вся платформа (код-ревью #1455). `id` — это id
 * события самой организации, ей и так видный в ответе.
 *
 * Тип и половина журнала — потому что позиция имеет смысл только в той
 * выборке, где её выдали: в другой она молча отрезала бы строки.
 */
export const consentJournalCursorSchema = z
  .object({
    id: z.string().min(1).max(128),
    consentType: z.string().min(1).max(64).nullable(),
    scope: consentJournalScopeSchema.nullable(),
  })
  .strict();

/** Курсор на проводе — в запросе и в ответе: непрозрачная base64url-строка. */
export const consentJournalCursorParamSchema = z
  .string()
  .min(1)
  .max(512)
  .regex(/^[A-Za-z0-9_-]+$/);

/**
 * Код отказа на курсоре, которого сервер не узнаёт (битый, чужой выборки или
 * строка исчезла). Клиент по нему читает журнал заново с первой страницы, а
 * не повторяет тот же курсор.
 */
export const CONSENT_JOURNAL_INVALID_CURSOR = 'INVALID_CURSOR' as const;

export const listConsentEventsResponseSchema = z.object({
  events: z.array(consentEventSchema),
  /**
   * Курсор следующей страницы; `null` — дальше строк нет.
   */
  nextCursor: consentJournalCursorParamSchema.nullable(),
  /**
   * Сколько строк выборки стоит сейчас на месте курсора и выше. До этой
   * страницы клиент показал ровно столько; больше — над курсором записано
   * событие, которого журнал не увидит без перечитывания (где бы оно ни
   * встало), и клиент говорит об этом, а не молчит до перезагрузки.
   */
  rowsUpToCursor: z.number().int().min(0),
});

// ============================================================================
// Type Exports
// ============================================================================

export type ConsentEventType = z.infer<typeof consentEventTypeSchema>;
export type ConsentType = z.infer<typeof consentTypeSchema>;
export type ParticipantConsentType = z.infer<typeof participantConsentTypeSchema>;
export type ConsentJournalScope = z.infer<typeof consentJournalScopeSchema>;
export type ConsentJournalCursor = z.infer<typeof consentJournalCursorSchema>;
export type ListConsentEventsResponse = z.infer<typeof listConsentEventsResponseSchema>;
export type GrantableConsentType = z.infer<typeof grantableConsentTypeSchema>;
export type WithdrawableConsentType = z.infer<typeof withdrawableConsentTypeSchema>;
export type ConsentEvent = z.infer<typeof consentEventSchema>;
export type GrantConsentRequest = z.infer<typeof grantConsentRequestSchema>;
export type WithdrawConsentRequest = z.infer<typeof withdrawConsentRequestSchema>;
export type ConsentStatusResponse = z.infer<typeof consentStatusResponseSchema>;
export type MarketingSubscriptionResponse = z.infer<
  typeof marketingSubscriptionResponseSchema
>;
